<script setup>
import router from '@/router';
import {onMounted, ref, watch} from 'vue';
import {getLoggedUser, getToken, logout} from '@/services/AuthService';
import {Capacitor} from '@capacitor/core';
import {getTenantData} from '@/services/TenantService';
import {jwtDecode} from "jwt-decode";
import {showSuccessMessage} from "@/helper";
import UserService from "@/services/UserService";
import {useI18n} from 'vue-i18n'
import LangSwitcher from "@/views/LangSwitcher.vue";
import {bondsEnabled} from '@/utils/features';

const {t} = useI18n();

const host = window.location.host;
const ieDomain = localStorage.getItem('IE_DOMAIN')

const loggedUser = ref({});
const theme = ref({});

let offcanvasInstance = null;
const offcanvas = ref(null);

const defaultMenuItems = ref([
  {path: "/home", label: t('sidebar.overview'), roles: ['CLIENT']},
  // IMA-517 fáze 6: nabídka a vlastněné dluhopisy klienta
  {path: "/bonds", label: t('sidebar.bonds'), roles: ['CLIENT'], feature: 'bonds'},
  {path: "/my-documents", label: t('sidebar.my_documents'), roles: ['CLIENT']},
  {path: "/my-service-requirements", label: t('sidebar.service_requests'), roles: ['CLIENT']},
  {path: "/advisor/service-requirements", label: t('sidebar.service_requests'), roles: ['INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER']},
  // IMA-517 fáze 7: dluhopisy, které poradce může nabízet (BE: AdvisorBondController::ADVISOR_ROLES)
  {path: "/advisor/bonds", label: t('sidebar.bonds'), roles: ['INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER'], feature: 'bonds'},
  // {path: "/back-office/service-requirements/history", label: t('sidebar.service_requests_history'), roles: ['SUPERADMIN', 'ADMIN']},
  {path: "/back-office/service-requirements", label: t('sidebar.service_requests'), roles: ['BACK_OFFICE', 'SUPERADMIN', 'ADMIN']},
  {path: "/clients", label: t('sidebar.clients'), roles: ['SUPERADMIN', 'ADMIN', 'INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER', 'BACK_OFFICE']},
  {path: "/clients/archive", label: t('sidebar.clients_archive'), roles: ['SUPERADMIN']},
  {path: "/reports", label: t('sidebar.reports'), roles: ['SUPERADMIN', 'ADMIN', 'INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER', 'BACKOFFICE']},
  {path: "https://portal.in-equity.cz", label: t('sidebar.portal_in_equity'), roles: ['SUPERADMIN', 'ADMIN', 'INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER'], newWindow: true, hiddenForBidli: true},
  {path: "/upload-reports", label: t('sidebar.upload_reports'), roles: ['SUPERADMIN', 'ADMIN']},
  {
    path: "/documents",
    label: t('sidebar.documents'),
    open: false,
    roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE'],
    children: [
      {path: "/documents", label: t('sidebar.to_processing'), filter: "to-processing"},
      {path: "/documents", label: t('sidebar.in_solution'), filter: "in-solution"},
      {path: "/documents", label: t('sidebar.archived'), filter: "archived"}
    ]
  },
  {
    path: "/documents/templates",
    label: t('sidebar.templates'),
    open: false,
    roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE'],
    children: [
      {path: "/documents/templates", label: t('sidebar.templates')},
      {path: "/documents/templates/categories", label: t('sidebar.category')}
    ]
  },
  {
    path: "/forms/templates",
    label: t('sidebar.forms'),
    open: false,
    roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE'],
  },
  {path: "/intermediaries", label: t('sidebar.intermediaries'), roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE']},
  {path: "/intermediary-people", label: t('sidebar.intermediaries_people'), roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE']},
  // IMA-510: MANAGER spravuje administraci celého vlastního SFA (cesta se dopočítá na vlastní
  // intermediary.id) - placeholder cesta je záměrně jiná než "/intermediaries" (admin položka výše),
  // ať nesdílí stejnou počáteční hodnotu před přepsáním v onMounted.
  {path: "/my-sfa-admin-placeholder", label: t('sidebar.my_sfa_admin'), roles: ['INTERMEDIARY_MANAGER'], dynamicOwnIntermediary: true},
  // IMA-510: LEADER spravuje jen vlastní podstrom poradců.
  {path: "/my-advisors", label: t('sidebar.my_advisors'), roles: ['INTERMEDIARY_LEADER']},
  // IMA-510: MANAGER navíc vidí přímý seznam zástupců celého SFA (stejná stránka jako u LEADERa -
  // backend seznam podle role omezí odlišně, viz IntermediaryPersonController::visiblePersonIds()).
  {path: "/my-advisors", label: t('sidebar.my_sfa_advisors'), roles: ['INTERMEDIARY_MANAGER']},
  {path: "/strategies", label: t('sidebar.strategies'), roles: ['SUPERADMIN', 'ADMIN']},
  // IMA-517: správa dluhopisů v administraci (dřív „Cenné papíry“; adresa /securities zůstala kvůli uloženým odkazům);
  // BE povoluje stejné role (BondController::MANAGE_ROLES). Klient a poradce mají vlastní menu Dluhopisy výše.
  {path: "/securities", label: t('sidebar.bonds'), roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE'], feature: 'bonds'},
  {path: "/users", label: t('sidebar.users'), roles: ['SUPERADMIN', 'ADMIN']},
  {path: "/intermediary-zone", label: t('sidebar.intermediary_zone'), roles: ['INTERMEDIARY', 'INTERMEDIARY_ADMIN', 'INTERMEDIARY_LEADER', 'INTERMEDIARY_MANAGER'], href: true},
  {path: "/payment-terms", label: t('sidebar.payment_terms'), roles: ['CLIENT']},
  {
    path: "/contract-types",
    label: t('sidebar.contract_types'),
    open: false,
    roles: ['SUPERADMIN', 'ADMIN', 'BACK_OFFICE'],
  },
  {path: "/notification-settings", label: t('sidebar.notification_settings'), roles: ['SUPERADMIN', 'ADMIN']},
  {path: "/settings", label: t('sidebar.settings'), roles: ['SUPERADMIN', 'ADMIN', 'CLIENT', 'INTERMEDIARY', 'INTERMEDIARY_ADMIN'], hiddenOnDesktop: true},
]);

const menuItems = ref([]);

onMounted(() => {
  offcanvasInstance = new bootstrap.Offcanvas(offcanvas.value);
  getTenantData()
      .then((response) => {
        theme.value = response.theme;
      });
  getLoggedUser()
      .then((userResponse) => {
        loggedUser.value = userResponse;

        const isBidli = window.location.hostname.includes('biginvest');
        // "Nastavení" je na desktopu prázdné a poradci/klienti ho nepotřebují — ponechat
        // jen adminům (kde slouží k nastavení SO/ASO) a v mobilní appce (Face ID nastavení).
        const isAdmin = ['SUPERADMIN', 'ADMIN'].includes(loggedUser.value.role);
        defaultMenuItems.value.forEach(menuItem => {
          if (menuItem.hiddenForBidli && isBidli) {
            return;
          }
          // dluhopisy jen v instancích, kde je BE nabízí (ne B.I.G. Invest SK/CZ – i v mobilní aplikaci, kde hostname nepomůže)
          if (menuItem.feature === 'bonds' && !bondsEnabled(loggedUser.value)) {
            return;
          }
          if (menuItem.hiddenOnDesktop && !Capacitor.isNativePlatform() && !isAdmin) {
            return;
          }
          if (menuItem.path === `/intermediary-zone`) {
            if (!loggedUser.value.intermediary?.code) {
              return;
            }
            menuItem.path = `https://produkty.in-equity.cz/?tenant_code=${loggedUser.value.intermediary.code}`;
          }
          // IMA-510: MANAGER admin. položka vede vždy na detail vlastního SFA, ne na cizí.
          if (menuItem.dynamicOwnIntermediary) {
            if (!loggedUser.value.intermediary?.id) {
              return;
            }
            menuItem.path = `/intermediaries/${loggedUser.value.intermediary.id}/detail`;
          }
          if (menuItem.roles.includes(loggedUser.value.role)) {
            menuItems.value.push(menuItem);
          }
        });
      });
});

function openMenu() {
  offcanvasInstance?.show()
}


function navigateTo(to, query = {}, isHref = false, newWindow = false) {
  // Open external links in a new browser tab
  if (newWindow) {
    window.open(to, '_blank', 'noopener,noreferrer');
    return;
  }
  if (isHref) {
    window.location.href = to;
    return;
  }
  const button = document.getElementById('menuDismissButton');
  if (button) {
    button.click();
  }
  router.push({path: to, query: query});
}

function toggleSubmenu(item) {
  if (item.children) {
    item.open = !item.open;
  } else {
    navigateTo(item.path);
  }
}

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false
  }
  return token.impersonator;
}

function deimpersonate() {
  UserService.deimpersonate().then(response => {
    showSuccessMessage(response.message);
  }, error => {
  });
}

</script>

<template>
  <nav id="navbarSupportedContent" class="navbar fixed-top">
    <div class="row h-100">
      <div class="col-3">
        <button class="navbar-toggler navbar__toggler" type="button" @click="openMenu" aria-controls="offcanvasNavbar">
          <i class="mdi mdi-menu"></i>
        </button>
      </div>
      <div class="col-6 h-100 text-center">
        <div class="navbar__logo">
        </div>
      </div>
      <div class="col-3 text-end">
        <button class="navbar-toggler navbar__toggler notifications__toggler" type="button" @click="navigateTo('/notifications')">
          <i class="mdi mdi-bell"></i>
        </button>
      </div>
    </div>
  </nav>
  <div class="offcanvas sidebar offcanvas-start" ref="offcanvas" tabindex="-1" id="offcanvasNavbar" aria-labelledby="offcanvasNavbarLabel">
    <div class="offcanvas-header">
      <div class="sidebar__header">
        <div class="sidebar__logo" v-if="theme && theme.logoBase64">
          <img :src="theme.logoBase64" v-if="theme && theme.logoBase64" alt=""/>
        </div>
        <div class="sidebar__user">
          <div class="sidebar__user">
            <div class="sidebar__user__name d-flex align-content-between">
              <span>{{ loggedUser?.name }}</span>
              <button v-if="impersonator()" type="button" @click="deimpersonate()" class="btn btn-warning btn-sm" style="height: 25px; line-height: 25px; margin-left: 10px;">
                <span class="mdi mdi-domino-mask"></span>
                {{ impersonator()?.name }}
                <span class="mdi mdi-login"></span>
              </button>
            </div>
            <div class="sidebar__user__info">
              {{ loggedUser?.email }}
<!--
              <br/><span v-if="loggedUser?.email?.includes('prodelal') || loggedUser?.email === 'kozusnikjan@gmail.com' || loggedUser?.email?.includes('example')">{{ host }}</span>
              <br/><span v-if="loggedUser?.email?.includes('prodelal') || loggedUser?.email === 'kozusnikjan@gmail.com' || loggedUser?.email?.includes('example')">{{ ieDomain }}</span>
-->

            </div>
          </div>

          <button id="menuDismissButton" type="button" class="sidebar__header__close btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
      </div>
    </div>


    <div class="offcanvas-body">
      <div class="sidebar__content">
        <ul class="sidebar__nav nav">
          <li v-for="item in menuItems" :key="item.path" class="sidebar__nav__item nav-item">
            <div class="nav-link-container">
              <a
                  v-if="item.children?.length"
                  @click="toggleSubmenu(item)"
                  class="nav-link d-flex justify-content-between"
                  :class="{ 'has-children': item.children }"
              >
                <span>{{ item.label }}</span>
                <span v-if="item.children" class="submenu-icon" :class="{ 'rotated': item.open }">▼</span>
              </a>
              <a
                  v-if="!item.children?.length"
                  @click="navigateTo(item.path, null, item.href, item.newWindow)"
                  class="nav-link d-flex justify-content-between"
                  :class="{ 'has-children': item.children }"
              >
                <span>{{ item.label }}</span>
              </a>


            </div>

            <ul v-if="item.children && item.open" class="submenu">
              <li v-for="subItem in item.children" :key="subItem.path" class="nav-item">
                <a
                    @click="navigateTo(subItem.path, { filter: subItem.filter })"
                    class="nav-link"
                    data-bs-dismiss="offcanvas"
                >
                  {{ subItem.label }}
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div v-if="false" class="w-100 d-flex justify-content-center mb-3">
        <LangSwitcher/>
      </div>
    </div>

    <div class="offcanvas-footer">
      <div class="sidebar__footer">
        <button @click="logout()" data-bs-dismiss="offcanvas" type="button" class="btn btn-transparent btn-text-red w-100">
          <i class="mdi mdi-logout"></i>
          {{ t('sidebar.log_out') }}
        </button>
      </div>
    </div>
  </div>
</template>
